import { Injectable, Optional } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '../security/auth.service'
import {Router} from "@angular/router";
import {FORBIDDEN, FORBIDDEN_MESSAGE} from "../common/Constants";
import {getMedicationBatchUploadDisplayMessage} from "../medication-alerts/batch-upload.utils";
import {MessageHandlerService} from "../services/message-handler.service";
import {ProgressBarService} from "../services/progress-bar.service";
import {PostHogService} from "../services/telemetry/posthog.service";
import {environment} from "../../environments/environment";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(private authenticationService: AuthService, protected router: Router,
                private messageHandlerService: MessageHandlerService,
                private readonly progressBarService: ProgressBarService,
                @Optional() private posthogService?: PostHogService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const startTime = Date.now();
        return next.handle(request).pipe(catchError(err => {
              // Capture HTTP API failures in PostHog (ignoring telemetry tunnel calls and 401 signouts)
              const tunnelUrl = environment.endPoints?.telemetryTunnel;
              const isTunnel = !!tunnelUrl && !!request.url?.startsWith(tunnelUrl);
              const isSignOut401 = err?.status === 401;

              if (this.posthogService && !isTunnel && !isSignOut401) {
                const safeUrl = this.posthogService.sanitizeRouteUrl(request.url);
                const durationMs = Date.now() - startTime;
                const user = this.authenticationService.loggedInUser;
                const agency =
                  user?.agency_roles?.find((ar) => ar.defaultAgency)?.agency?.agencyID ||
                  user?.agency_roles?.[0]?.agency?.agencyID ||
                  undefined;

                this.posthogService.captureError(
                  { name: 'HttpApiError', message: `HTTP ${err?.status} ${request.method} ${safeUrl}` },
                  {
                    error_type: 'HttpApiError',
                    http_status: err?.status,
                    url: safeUrl,
                    method: request.method,
                    duration_ms: durationMs,
                    agency,
                  },
                );
              }

              if(err.status === 401){
                this.authenticationService.signOut();
              }

            // Audit writes from the public signing page run in the background. Their failure must never
            // put an error in front of a patient mid-signing; the page logs it and carries on.
            if (request.url?.includes('patient_esign/audit_log')) {
              this.progressBarService.setProgressBarVisibility(false);
              return throwError(() => err);
            }

            // IOHB-1754: App Configuration surfaces its own errors, the same way
            // medication-alerts/batch-upload does below — hide the progress bar
            // and rethrow the untouched HttpErrorResponse.
            //
            // That API answers with `errorMessage`, not `message`, so the generic
            // branch below could not read it and fell back to FORBIDDEN_MESSAGE
            // ("Sorry, The file has been removed OR expired.") on every failure.
            // Returning EMPTY then meant neither the component's next nor its
            // error callback ran, so its loading/saving flags stayed true and the
            // Publish and reload buttons were dead for the rest of the session.
            //
            // Placed before the status check rather than inside it, unlike the
            // batch-upload hatch: the optimistic-concurrency 409 fell through to
            // the bottom of this method, which rethrows a bare string, leaving
            // `err.status` undefined and the component's 409 branch unreachable.
            if (request.url?.includes('user/app-config')) {
              this.progressBarService.setProgressBarVisibility(false);
              return throwError(() => err);
            }

            // The Logship status panel shows its own error and toast, and needs the status: a 502/503/504 or
            // no answer at all (status 0) means the service is down. Below, those lost their status (rethrown
            // as a bare string), or a body-less one threw a TypeError, and showed no toast.
            if (request.url?.includes('qa_scrubber/logship-status')) {
              this.progressBarService.setProgressBarVisibility(false);
              return throwError(() => err);
            }

            if (err.status === 403 || err.status === 400 || err.status === 500) {
              const isBatchUpload = err.status === 400 && request.url?.includes('medication-alerts/batch-upload');
              if (isBatchUpload && Array.isArray(err?.error?.errors) && err.error.errors.length > 0) {
                this.progressBarService.setProgressBarVisibility(false);
                return throwError(() => err);
              }

              const rawMessage = err?.error?.message;
              const displayMessage = isBatchUpload
                ? getMedicationBatchUploadDisplayMessage(rawMessage)
                : (rawMessage || FORBIDDEN_MESSAGE);
              this.messageHandlerService.generateToastMessage(
                'error',
                isBatchUpload ? '' : FORBIDDEN,
                displayMessage
              );
              this.progressBarService.setProgressBarVisibility(false);
              // Return EMPTY instead of null to prevent RxJS "null where stream expected" error
              // This completes the Observable without emitting, so components won't trigger error handlers
              return EMPTY;
            }

            const error = err.error.message || err.statusText;
            return throwError(error);
        }))
    }
}
